import {
  Button,
  ColorPicker,
  DropZone,
  HSBAColor,
  hsbToHex,
  Icon,
  InlineError,
  LegacyCard,
  Popover,
  rgbToHsb,
  Text,
} from "@shopify/polaris"
import classNames from "classnames"
import { observer } from "mobx-react"
import { uploadFilesToR2, useRedHatTextFont, XSmallIcon } from "polaris-enhance"
import { Fragment, useCallback, useState } from "react"

import stores from "@/stores"
import toast from "@/utils/toast"

import StepHeader from "../components/StepHeader"
import { getThemeColorOptions } from "../config"

const MAX_LOGO_SIZE = 10 * 1024 * 1024
const ACCEPTED_IMAGE_TYPES =
  "image/gif,image/jpeg,image/png,image/webp,image/svg+xml"
const customValidator = (file: File) => file.size <= MAX_LOGO_SIZE

function hexToRgb(value: string) {
  const clean = value.replace("#", "")
  if (clean.length === 3) {
    const red = parseInt(clean[0] + clean[0], 16)
    const green = parseInt(clean[1] + clean[1], 16)
    const blue = parseInt(clean[2] + clean[2], 16)
    return { red, green, blue }
  }
  const red = parseInt(clean.slice(0, 2), 16)
  const green = parseInt(clean.slice(2, 4), 16)
  const blue = parseInt(clean.slice(4, 6), 16)
  return { red, green, blue }
}

const PortalPreview = observer(
  (props: { logoUrl?: string; themeColor: string }) => {
    const { logoUrl, themeColor } = props
    const themeColors = getThemeColorOptions()
    const matchedPreset = themeColors.find((color) => color.hex === themeColor)
    const isCustom = !matchedPreset
    const selectedColor = matchedPreset || themeColors[1]
    const customHex = isCustom ? themeColor : null

    return (
      <div className="preview-container">
        <div className="flex h-10 min-w-0 items-center gap-[6px] bg-[#f3f3f3] px-3 sm:px-5 ">
          <div className="flex shrink-0 items-center gap-[6px]">
            <span className="h-[10px] w-[10px] rounded-full bg-[#ff6159]" />
            <span className="h-[10px] w-[10px] rounded-full bg-[#ffbd2e]" />
            <span className="h-[10px] w-[10px] rounded-full bg-[#28c840]" />
          </div>
          <div className="mx-auto min-w-0 truncate rounded-sm bg-white px-3 py-1 text-center text-xs text-[var(--p-color-text-secondary)] border border-solid border-[#E3E3E3]">
            {t("YourstoreComAppsParcelpanelReturns")}
          </div>
          <div className="w-[42px] shrink-0" aria-hidden="true" />
        </div>
        <div className="bg-[#fff1e3] px-3 py-2 text-[13px] text-[#5e4200]">
          {t("⚠PreviewOnly—InLiveFlowCustomersComplete")}
        </div>
        <div className="mx-auto flex max-w-[360px] flex-col gap-3 px-5 py-6">
          {logoUrl ? (
            <img
              alt={t("StoreLogo")}
              className="mx-auto max-h-12 max-w-[200px] object-contain"
              src={logoUrl}
            />
          ) : (
            <Text as="h2" alignment="center" variant="headingMd">
              {t("ReturnCenter_0")}
            </Text>
          )}

          <Text as="p" variant="bodyMd">
            {t("OrderNumber_0")}
          </Text>
          <div className="rounded-lg border border-solid border-[var(--p-color-border)] px-3 py-2 text-[14px] text-[var(--p-color-text-secondary)]">
            #1042
          </div>
          <Text as="p" variant="bodyMd">
            {t("EmailOrPhone")}
          </Text>
          <div className="rounded-lg border border-solid border-[var(--p-color-border)] px-3 py-2 text-[14px] text-[var(--p-color-text-secondary)]">
            {t("CustomerExampleCom")}
          </div>
          <button
            className={classNames(
              "h-8 rounded-lg border-0 text-[13px] font-medium text-white",
              !isCustom && selectedColor.bgClass,
            )}
            style={customHex ? { backgroundColor: customHex } : undefined}
            type="button"
          >
            {t("StartYourReturn")}
          </button>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-1 gap-y-0 bg-[#f3f3f3] px-5 py-3 text-[10px] text-[var(--p-color-text-secondary)]">
          {[
            t("VerifyOrder"),
            t("SelectItemsAndReason"),
            t("SelectReturnSolution"),
            t("ChooseShippingMethods"),
            t("Submit"),
          ].map((label, index) => (
            <Fragment key={index}>
              <span
                className={classNames(
                  !isCustom &&
                    index === 0 &&
                    `${selectedColor.textClass} ${selectedColor.value}`,
                )}
                style={
                  isCustom && index === 0 && customHex
                    ? { color: customHex }
                    : undefined
                }
              >
                <span className="preview-number">{index + 1}</span> {label}
              </span>
              {index < 4 && <span className="split-line">—</span>}
            </Fragment>
          ))}
        </div>
      </div>
    )
  },
)

const StepThree = observer(
  (props: { onBack: () => void; onContinue: () => void }) => {
    const { onBack, onContinue } = props
    const onboarding = stores.OnboardingStore
    const [uploading, setUploading] = useState(false)
    const [logoError, setLogoError] = useState("")
    const [customPickerActive, setCustomPickerActive] = useState(false)
    const [pickerHsb, setPickerHsb] = useState({
      brightness: 1,
      hue: 220,
      saturation: 1,
    })

    const themeColor = onboarding.themeColor
    const logoUrl = onboarding.logoUrl
    const matchedPreset = getThemeColorOptions().find(
      (c) => c.hex === themeColor,
    )
    const customColor = matchedPreset
      ? null
      : themeColor.replace(/^#/, "").toUpperCase()

    useRedHatTextFont()

    const handleDropAccepted = useCallback(
      async (files: File[]) => {
        const file = files[0]
        if (!file) return
        setLogoError("")
        setUploading(true)
        onboarding.setLogoUrl("")
        try {
          const [result] = await uploadFilesToR2([file])
          if (result?.ossUrl) {
            onboarding.setLogoUrl(result.ossUrl)
            toast(t("UploadSuccessfully"))
          } else {
            setLogoError(t("UploadFailedPleaseTryAgain"))
          }
        } catch (error) {
          setLogoError(t("UploadFailedPleaseTryAgain"))
        } finally {
          setUploading(false)
        }
      },
      [onboarding],
    )

    const handleDropRejected = useCallback((files: File[]) => {
      const file = files[0]
      if (!file) return
      setLogoError(
        file.size > MAX_LOGO_SIZE
          ? t("FileIsTooLarge")
          : t("UnsupportedFileFormat"),
      )
    }, [])

    const syncPickerFromCurrent = () => {
      if (!themeColor.startsWith("#")) return
      const { red, green, blue } = hexToRgb(themeColor)
      if (!isNaN(red) && !isNaN(green) && !isNaN(blue)) {
        setPickerHsb(rgbToHsb({ red, green, blue }))
      }
    }

    const toggleCustomPicker = () => {
      if (!customPickerActive) {
        syncPickerFromCurrent()
      }
      setCustomPickerActive((active) => !active)
    }

    const handleCustomColorChange = (color: HSBAColor) => {
      setPickerHsb(color)
      const hex = hsbToHex(color).replace("#", "").toUpperCase()
      onboarding.setThemeColor(`#${hex}`)
    }

    const handleContinue = async () => {
      onboarding.saveStep3()
      onContinue()
    }

    return (
      <div id="step3" className="flex flex-col gap-5">
        <StepHeader
          description={t("CustomizeReturnPortalsLookAndPreview")}
          title={t("ThisIsWhatYourCustomersWillSee")}
        />
        <LegacyCard>
          <div className="flex justify-between  p-5 gap-5">
            <div className="flex flex-col gap-2 shrink-0">
              <Text as="p" tone="subdued" variant="bodyMd">
                {t("YourStoreLogo")}
              </Text>
              <div className="flex flex-col gap-1">
                <div
                  className={classNames(
                    "dropzone-container group relative",
                    { error: !!logoError },
                    "w-[160px]",
                  )}
                >
                  <DropZone
                    accept={ACCEPTED_IMAGE_TYPES}
                    allowMultiple={false}
                    customValidator={customValidator}
                    disabled={uploading}
                    error={!!logoError}
                    errorOverlayText={logoError || t("FileIsTooLarge")}
                    onDropAccepted={handleDropAccepted}
                    onDropRejected={handleDropRejected}
                    variableHeight
                  >
                    {logoUrl ? (
                      <div className="inline-flex h-9 items-center justify-center px-3">
                        <img
                          alt={t("StoreLogo")}
                          className="max-h-7 max-w-[160px] object-contain"
                          src={logoUrl}
                        />
                      </div>
                    ) : (
                      <div
                        className={classNames(
                          "placeholder flex h-9 w-full items-center justify-center px-4 text-[13px] text-[var(--p-color-text)]",
                          uploading && "opacity-50",
                        )}
                      >
                        {uploading ? t("Uploading") : t("UploadLogo")}
                      </div>
                    )}
                  </DropZone>
                  {logoUrl && (
                    <span
                      aria-label={t("RemoveLogo")}
                      className="absolute -right-2 -top-2 z-10 hidden h-5 w-5 cursor-pointer items-center justify-center rounded-full bg-[rgba(74,74,74,0.6)] group-hover:flex"
                      onClick={(event) => {
                        event.stopPropagation()
                        onboarding.setLogoUrl(undefined)
                      }}
                    >
                      <Icon source={XSmallIcon} />
                    </span>
                  )}
                </div>
                {logoError && (
                  <InlineError fieldID="logo-upload" message={logoError} />
                )}
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <Text as="p" tone="subdued" variant="bodyMd">
                {t("ThemeColor")}
              </Text>
              <div className="flex flex-wrap items-center gap-2">
                {getThemeColorOptions().map((color) => {
                  return (
                    <button
                      className={classNames(
                        "flex h-7 w-7 sm:h-10 sm:w-10 cursor-pointer items-center justify-center rounded-full border-0 text-white",
                        color.bgClass,
                      )}
                      key={color.value}
                      onClick={() => {
                        onboarding.setThemeColor(color.hex)
                      }}
                      type="button"
                    >
                      {themeColor === color.hex ? "✓" : ""}
                    </button>
                  )
                })}
                <Popover
                  active={customPickerActive}
                  activator={
                    <button
                      aria-label={
                        customColor
                          ? t("RePickCustomColor")
                          : t("PickCustomColor")
                      }
                      className={classNames(
                        "flex h-7 w-7 sm:h-10 sm:w-10 cursor-pointer items-center justify-center rounded-full text-md sm:text-lg",
                        customColor
                          ? "text-white border-0"
                          : "border border-dashed border-[var(--p-color-border-hover)] bg-white text-[var(--p-color-text)]",
                      )}
                      onClick={toggleCustomPicker}
                      style={
                        customColor
                          ? { backgroundColor: `#${customColor}` }
                          : undefined
                      }
                      type="button"
                    >
                      {customColor ? "✓" : "+"}
                    </button>
                  }
                  onClose={toggleCustomPicker}
                  preferredAlignment="left"
                >
                  <div className="p-2 w-[220px]">
                    <ColorPicker
                      allowAlpha={false}
                      color={pickerHsb}
                      onChange={handleCustomColorChange}
                    />
                  </div>
                </Popover>
              </div>
            </div>
          </div>
          <PortalPreview logoUrl={logoUrl} themeColor={themeColor} />
        </LegacyCard>
        <div className="flex items-center justify-between">
          <Button
            disabled={onboarding.isSaving}
            onClick={onBack}
            variant="tertiary"
          >
            {t("Back_0")}
          </Button>
          <div className="blueButton">
            <Button
              disabled={onboarding.isSaving}
              loading={onboarding.isSaving}
              onClick={handleContinue}
              variant="tertiary"
            >
              {t("Continue_0")}
            </Button>
          </div>
        </div>
      </div>
    )
  },
)

export default StepThree
